import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BillingService } from '../../core/billing.service';

/**
 * The ONE Stripe SetupIntent / Elements card-add flow in the app. Extracted
 * from features/admin/billing/billing.ts (AdminBilling still uses this
 * component internally — see billing.ts) so the start-project activation
 * modal can embed the exact same flow inline instead of building a second
 * Stripe integration (owner-approved billing simplification, point 3).
 *
 * Auto-starts on init (mounts the Stripe Elements payment form immediately)
 * — callers that want a "click to reveal" affordance (like the billing
 * page's "Add Method" button on top of an existing card) should keep that
 * button themselves and only render <app-add-card-step> once clicked.
 */
@Component({
  selector: 'app-add-card-step',
  standalone: true,
  imports: [],
  template: `
    <div class="add-card-step">
      <div id="{{ mountId }}" class="add-card-step-mount"></div>
      @if (cardError()) {
        <div class="add-card-step-error">{{ cardError() }}</div>
      }
      <div class="add-card-step-actions">
        @if (showCancel) {
          <button type="button" class="btn btn-outline" (click)="cancel.emit()" [disabled]="cardSaving()">
            Cancel
          </button>
        }
        <button type="button" class="btn btn-primary" (click)="saveCard()" [disabled]="cardSaving() || !cardReady()">
          @if (cardSaving()) {
            <span class="add-card-step-spinner" aria-hidden="true"></span> Confirming card…
          } @else {
            Save Payment Method
          }
        </button>
      </div>
    </div>
  `,
  styles: [`
    .add-card-step { display: flex; flex-direction: column; gap: 10px; }
    .add-card-step-mount {
      min-height: 44px; padding: 10px 14px; background: var(--rp-surface);
      border: 1px solid var(--rp-border); border-radius: 10px;
    }
    .add-card-step-error { color: var(--rp-danger); font-size: 13px; }
    .add-card-step-actions { display: flex; gap: 8px; justify-content: flex-end; }
    .add-card-step-spinner {
      display: inline-block; width: 12px; height: 12px; margin-right: 6px;
      /* On-brand track derived from the button's own text color (F8) —
         no raw rgba: the button is on-brand fill, so currentColor at 40%
         reads as a faded track under any theme's primary-button color. */
      border: 2px solid color-mix(in srgb, currentColor 40%, transparent); border-top-color: currentColor;
      border-radius: 50%; animation: add-card-step-spin 0.8s linear infinite;
      vertical-align: -1px;
    }
    @keyframes add-card-step-spin { to { transform: rotate(360deg); } }
  `],
})
export class AddCardStep implements OnInit, OnDestroy {
  private billing = inject(BillingService);
  private platformId = inject(PLATFORM_ID);

  /** DOM id for the Stripe Elements mount point — must be unique on the
   * page if more than one AddCardStep could ever be rendered at once
   * (not expected today, but parameterized defensively). */
  @Input() mountId = 'stripe-card-element-step';
  /** Show a Cancel button that emits (cancel) instead of just Save. */
  @Input() showCancel = false;
  /** Fires once the payment method is confirmed AND set as the org's
   * default — the caller decides what happens next (e.g. retry the
   * activation call). */
  @Output() cardAdded = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  cardReady = signal(false);
  cardError = signal<string | null>(null);
  cardSaving = signal(false);

  private stripe: any = null;
  private elements: any = null;
  private cardElement: any = null;

  ngOnInit(): void {
    this.start();
  }

  ngOnDestroy(): void {
    if (this.cardElement) {
      this.cardElement.destroy();
      this.cardElement = null;
    }
  }

  private async start(): Promise<void> {
    this.cardError.set(null);
    this.cardReady.set(false);
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const intent = await this.billing.createSetupIntent();
      const { loadStripe } = await import('@stripe/stripe-js');
      this.stripe = await loadStripe(intent.publishable_key);
      if (!this.stripe) {
        this.cardError.set('Failed to load Stripe');
        return;
      }
      this.elements = this.stripe.elements({ clientSecret: intent.client_secret });
      this.cardElement = this.elements.create('payment');
      await new Promise<void>(resolve => setTimeout(resolve, 50));
      const container = document.getElementById(this.mountId);
      if (container) {
        this.cardElement.mount(container);
        this.cardReady.set(true);
      }
    } catch {
      this.cardError.set('Failed to initialize payment form');
    }
  }

  async saveCard(): Promise<void> {
    if (!this.stripe || !this.elements) return;
    this.cardSaving.set(true);
    this.cardError.set(null);

    try {
      const { error } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (error) {
        this.cardError.set(error.message || 'Setup failed');
        return;
      }

      const methods = await this.billing.getPaymentMethods();
      if (methods.length === 1 && methods[0]) {
        await this.billing.setDefaultPaymentMethod(methods[0].id);
      }
      this.cardAdded.emit();
    } catch {
      this.cardError.set('Failed to save payment method');
    } finally {
      this.cardSaving.set(false);
    }
  }
}
